import React, {memo, useCallback, useEffect, useState} from "react";
import {isEmpty} from "lodash";
import PropTypes from "prop-types";
import {
  Col,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import {toast} from "react-toastify";
import {_Buttons, MODAL} from "../../constants/appConstants";
import TimezoneList from "../../constants/timezones.json";
import {messages} from "../../constants";
import {
  sendAnalogBankData,
  sendEditControllerModalData,
  requestAnalogBank,
  resetController,
  requestCategories,
  requestSensorGroupsByCategory,
} from "../../redux/Actions/controllers.actions";
import {toggleModal} from "../../redux/Actions/general.actions";
import {requestUnclaimController} from "../../redux/Actions/sites.actions";
import {
  getAnalogBanks,
  getSelectedController,
  getCategoriesList,
  getFormattedSummaryData,
} from "../../redux/Selectors/controllers.selectors";
import {getCurrentSite} from "../../redux/Selectors/sites.selectors";
import ZeusButton from "../Button/Button";
import Loader from "../Loader";
import InputCreator from "../Forms/components/InputCreator";
import SelectCreator from "../Forms/components/SelectCreator";
import ToggleCreator from "../Forms/components/ToggleCreator";
import ProtectedContent, {usePermissionCheck} from "../protectedContent";

const EditController = (props) => {
  const {ioTypes, toggleModals, open, toggle} = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const canEditController = usePermissionCheck(["change_systeminventory"]);
  const controller_data = useSelector(getSelectedController);
  const {name, properties, category = "", groups = []} = controller_data;
  const {state = {}, timezone = ""} = properties;
  const {is_ready} = state;
  const selectedSite = useSelector(getCurrentSite);
  const analog_banks = useSelector(getAnalogBanks);
  const categories = useSelector(getCategoriesList);
  const summaryDetails = useSelector(getFormattedSummaryData);
  const [groupList, setGroupList] = useState([]);
  const [payload, setPayload] = useState({
    name,
    timezone,
    category_id: category,
    groups_id: groups,
  });
  const [submitted, setSubmitted] = useState(false);

  const url = location.pathname;

  const fetchSensorGroups = (category) => {
    if (category) {
      dispatch(requestSensorGroupsByCategory(category, setGroupList));
    }
  };

  useEffect(() => {
    dispatch(requestCategories());
    dispatch(requestAnalogBank());
    fetchSensorGroups(payload.category_id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeHandler = (event) => {
    const {value, name} = event.target;
    if (name === "category_id") {
      setPayload({...payload, [name]: value, groups_id: []});
      fetchSensorGroups(value);
    } else {
      setPayload({...payload, [name]: value});
    }
  };

  const onChangeBankHandler = (event) => {
    const {checked, name} = event.target;

    dispatch(sendAnalogBankData({[name]: checked}, toggle));
  };
  const editController = () => {
    if (
      !isEmpty(payload.name) &&
      !isEmpty(payload.timezone) &&
      payload.category_id &&
      !isEmpty(payload.groups_id)
    ) {
      dispatch(sendEditControllerModalData(payload, toggle));
    } else {
      toast.error("Please check required fields");
    }
  };

  const onFormSubmit = () => {
    setSubmitted(true);
    editController();
  };

  const handleSiteRoute = useCallback(() => {
    navigate(`/app/sites/${selectedSite.id}`);
  }, [navigate, selectedSite.id]);

  const handleHomeRoute = useCallback(() => {
    navigate("/app/home");
  }, [navigate]);

  const closeModal = useCallback(() => {
    dispatch(
      toggleModal({
        open: false,
        title: "",
        description: "",
      })
    );
    if (location?.state?.previous === "/app/home") {
      handleHomeRoute();
    } else {
      handleSiteRoute();
    }
  }, [dispatch, handleHomeRoute, handleSiteRoute, location.state?.previous]);

  const unClaimHandler = useCallback(() => {
    dispatch(requestUnclaimController(closeModal));
  }, [closeModal, dispatch]);

  const handleUnclaim = () => {
    dispatch(
      toggleModal(
        {
          open: true,
          title: messages.modals.title.confirmUnclaim,
          description: messages.modals.description.unclaimDescription,
        },
        unClaimHandler
      )
    );
  };

  const handleReset = () => {
    dispatch(resetController(toggle));
  };

  const showClone = !isEmpty(ioTypes) && isEmpty(summaryDetails);

  return (
    <Modal unmountOnClose={true} isOpen={open} size={"lg"}>
      <Loader />
      <ModalHeader>{MODAL.CONTROLLER.EDIT}</ModalHeader>
      <ModalBody>
        <div className="pl-lg-1">
          <Row>
            <Col lg="12">
              <FormGroup>
                <InputCreator
                  id="name"
                  label="Appliance Name"
                  label_for="name"
                  placeholder="Cooling Tower"
                  type="text"
                  value={payload.name}
                  name="name"
                  isRequired={true}
                  onChange={onChangeHandler}
                  invalid={isEmpty(payload.name) && submitted}
                  large
                  data-cy="Edit_ControllerName"
                  disabled={!canEditController}
                />
              </FormGroup>
            </Col>
            <Col lg="12">
              <FormGroup>
                <SelectCreator
                  id={"category"}
                  label={"Category"}
                  placeholder={"Choose a category"}
                  name={"category_id"}
                  options={categories}
                  onChange={onChangeHandler}
                  value={payload.category_id}
                  classNamePrefix={"category"}
                  invalid={isEmpty(payload.category) && submitted}
                  large
                  isRequired
                />
              </FormGroup>
            </Col>
            <Col lg="12">
              <FormGroup>
                <SelectCreator
                  id={"group"}
                  label={"Groups"}
                  placeholder={"Select group(s)"}
                  name={"groups_id"}
                  options={groupList}
                  onChange={onChangeHandler}
                  value={payload.groups_id}
                  classNamePrefix={"group"}
                  isMulti={true}
                  large
                  isRequired
                  help_text={"You can configure each group’s profile from the Tools tab."}
                />
              </FormGroup>
            </Col>
            <Col lg="12">
              <FormGroup>
                <SelectCreator
                  id={"timezone"}
                  label="Timezone"
                  placeholder="Choose appliance timezone"
                  value={payload.timezone}
                  name="timezone"
                  options={TimezoneList}
                  isRequired={true}
                  onChange={onChangeHandler}
                  invalid={isEmpty(payload.timezone) && submitted}
                  large
                  classNamePrefix={"timezone"}
                  isDisabled={!canEditController}
                />
              </FormGroup>
            </Col>
            {analog_banks && (
              <Col lg="12">
                <FormGroup>
                  <div>
                    <label className="form-control-label" htmlFor="input-name">
                      Analog Output Banks
                    </label>
                  </div>
                  {analog_banks.map((bank, index) => {
                    return (
                      <ToggleCreator
                        id={index}
                        label={`Bank ${bank["slot"]}`}
                        checked={bank["value"]}
                        name={bank["slot"]}
                        onChange={onChangeBankHandler}
                        key={index}
                        disabled={!canEditController}
                      />
                    );
                  })}
                </FormGroup>
              </Col>
            )}
          </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        <Col>
          <Row>
            <div className="col-xs-12 col-lg-8">
              {is_ready === false && (
                <ZeusButton data-cy="reset-btn" onClick={handleReset} type="btn-danger">
                  {_Buttons.RESET}
                </ZeusButton>
              )}
              <ProtectedContent perms={["delete_systeminventory"]}>
                <ZeusButton data-cy="unclaim-btn" onClick={() => handleUnclaim()}>
                  {_Buttons.UNCLAIM}
                </ZeusButton>
              </ProtectedContent>
              <ZeusButton
                data-cy="telemetry-btn"
                onClick={() => {
                  navigate(`${url}/telemetry/`);
                }}
              >
                TELEMETRY
              </ZeusButton>
              {showClone && (
                <ProtectedContent perms={["clone_sensors_systeminventory"]}>
                  <ZeusButton data-cy="clone-profile-btn" onClick={toggleModals}>
                    CLONE PROFILE
                  </ZeusButton>
                </ProtectedContent>
              )}
            </div>
            <div className="col-xs-12 col-lg-4 mt-4 mt-lg-0" style={{textAlign: "right"}}>
              <ProtectedContent perms={["change_systeminventory"]}>
                <ZeusButton data-cy="submit-btn" onClick={onFormSubmit}>
                  {_Buttons.SAVE}
                </ZeusButton>
              </ProtectedContent>
              <ZeusButton data-cy="cancel-btn" onClick={toggle} type={"secondary"}>
                {_Buttons.CANCEL}
              </ZeusButton>
            </div>
          </Row>
        </Col>
      </ModalFooter>
    </Modal>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.open === nextProps.open;
}

EditController.propTypes = {
  ioTypes: PropTypes.array.isRequired,
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  toggleModals: PropTypes.func.isRequired,
};
export default memo(EditController, areEqual);
